import { Bike, Link2, Loader2, Package, ShoppingBag, Truck } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { SectionHeader } from '@/components/ui/section-header';
import { EmptyState } from '@/components/ui/empty-state';
import { ErrorState } from '@/components/ui/error-state';
import { StatusPill, type StatusTone } from '@/components/ui/status-pill';
import { form_error_message } from '@/lib/forms';
import { ImageCarousel } from '@/components/image-carousel';
import { use_my_orders, use_cancel_my_order } from '@/features/order/hooks';
import type { OrderStatus } from '@/features/order/types';
import { use_my_commerce_orders, use_cancel_commerce_shipment } from '@/features/commerce/hooks';
import type { CommerceOrder, CommerceOrderShipment, CommerceStatus } from '@/features/commerce/types';

const STATUS_TONE: Record<OrderStatus, StatusTone> = {
  PLACED: 'warning',
  ACCEPTED: 'info',
  OUT_FOR_DELIVERY: 'info',
  READY: 'info',
  COMPLETED: 'success',
  CANCELLED: 'destructive',
  REJECTED: 'destructive',
};

const STATUS_COPY: Record<OrderStatus, string> = {
  PLACED: 'Waiting for the restaurant to accept',
  ACCEPTED: 'Accepted — being prepared',
  OUT_FOR_DELIVERY: 'Out for delivery',
  READY: 'Ready for pickup',
  COMPLETED: 'Completed',
  CANCELLED: 'Cancelled',
  REJECTED: 'Declined by the restaurant',
};

const COMMERCE_STATUS_TONE: Record<CommerceStatus, StatusTone> = {
  PENDING_PAYMENT: 'warning',
  PLACED: 'warning',
  ACCEPTED: 'info',
  OUT_FOR_DELIVERY: 'info',
  READY: 'info',
  COMPLETED: 'success',
  CANCELLED: 'destructive',
  REJECTED: 'destructive',
};

/**
 * What to show for a commerce order. An unpaid order keeps its own PENDING_PAYMENT status:
 * its shipments already sit at PLACED (they are created with the order, before payment), so
 * the rollup alone made an unpaid order look confirmed.
 */
function commerce_display_status(o: { status: CommerceStatus; rollup_status: CommerceStatus }): CommerceStatus {
  return o.status === 'PENDING_PAYMENT' ? 'PENDING_PAYMENT' : o.rollup_status;
}

const COMMERCE_ROLLUP_COPY: Record<CommerceStatus, string> = {
  PENDING_PAYMENT: 'Payment pending — complete payment to confirm',
  PLACED: 'In progress — one or more shipments are being fulfilled',
  ACCEPTED: 'Accepted',
  OUT_FOR_DELIVERY: 'Out for delivery',
  READY: 'Ready',
  COMPLETED: 'All shipments completed',
  CANCELLED: 'Cancelled',
  REJECTED: 'Declined',
};

function fmt(iso: string): string {
  return new Date(iso).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' });
}

function CommerceShipmentCard({
  order,
  shipment,
  index,
  total,
}: {
  order: CommerceOrder;
  shipment: CommerceOrderShipment;
  index: number;
  total: number;
}) {
  const cancel_m = use_cancel_commerce_shipment();

  async function cancel() {
    if (!window.confirm('Cancel this shipment?')) return;
    await cancel_m.mutateAsync({ order_id: order.id, shipment_id: shipment.id });
  }

  // Vendors are hidden from the shopper — shipments are labelled by an ordinal, never a vendor name.
  const label = total > 1 ? `Shipment ${index + 1} of ${total}` : 'Your shipment';

  return (
    <Card data-testid={`commerce-shipment-card-${shipment.id}`} className="border-2 border-brand-outline-variant/15">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border p-3">
        <div className="flex flex-wrap items-center gap-2">
          <Truck className="h-4 w-4 text-primary" />
          <span className="text-sm font-semibold">{label}</span>
          <StatusPill tone={COMMERCE_STATUS_TONE[shipment.status]}>{shipment.status.replace(/_/g, ' ').toLowerCase()}</StatusPill>
        </div>
        <span className="font-heading font-bold tabular-nums">₹{shipment.subtotal_inr}</span>
      </div>
      <div className="p-3">
        <ul className="flex flex-col gap-1 text-sm">
          {shipment.lines.map((it) => (
            <li key={it.id} className="flex justify-between">
              <span>
                {it.quantity}× {it.name}
                {it.variant_label && <span className="ml-1 text-xs text-muted-foreground">{it.variant_label}</span>}
                {it.is_prescription_required && <span className="ml-1 text-xs font-semibold text-warning">Rx</span>}
              </span>
              <span className="tabular-nums text-muted-foreground">₹{it.line_total_inr}</span>
            </li>
          ))}
        </ul>
        {shipment.tracking_url ? (
          <a
            href={shipment.tracking_url}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-2 flex items-center gap-1 text-xs font-semibold text-primary hover:underline"
            data-testid={`commerce-shipment-tracking-${shipment.id}`}
          >
            <Link2 className="h-3.5 w-3.5" /> {shipment.tracking_ref || 'Track shipment'}
          </a>
        ) : (
          shipment.tracking_ref && (
            <p className="mt-2 text-xs text-muted-foreground" data-testid={`commerce-shipment-tracking-${shipment.id}`}>
              Tracking ref: {shipment.tracking_ref}
            </p>
          )
        )}
        {shipment.cancel_reason && <p className="mt-1 text-xs text-muted-foreground">Reason: {shipment.cancel_reason}</p>}
        {shipment.status === 'PLACED' && (
          <Button
            size="sm"
            variant="ghost"
            className="mt-2"
            disabled={cancel_m.isPending}
            onClick={cancel}
            data-testid={`cancel-shipment-${shipment.id}`}
          >
            Cancel shipment
          </Button>
        )}
      </div>
    </Card>
  );
}

export function MyOrdersPage() {
  const orders_q = use_my_orders();
  const cancel_m = use_cancel_my_order();
  const orders = orders_q.data ?? [];

  const commerce_orders_q = use_my_commerce_orders();
  const commerce_orders = commerce_orders_q.data ?? [];

  async function cancel(id: string) {
    if (!window.confirm('Cancel this order?')) return;
    await cancel_m.mutateAsync({ id });
  }

  const loading = orders_q.isLoading || commerce_orders_q.isLoading;
  // Fetch errors on one feed don't block the others from showing — only surface the error
  // state when every feed is unavailable and empty.
  const both_errored = orders_q.isError && commerce_orders_q.isError;
  const is_empty = !loading && !both_errored && orders.length === 0 && commerce_orders.length === 0;

  return (
    <div className="flex flex-col gap-5">
      <SectionHeader title="My orders" description="Your food and pharmacy orders and their status." />

      {loading ? (
        <Card>
          <div className="flex items-center justify-center p-10 text-sm text-muted-foreground">
            <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading…
          </div>
        </Card>
      ) : both_errored ? (
        <ErrorState
          data-testid="my-orders-error"
          title="Couldn't load orders"
          description={form_error_message(orders_q.error, 'Please try again.')}
        />
      ) : is_empty ? (
        <EmptyState
          data-testid="my-orders-empty"
          icon={ShoppingBag}
          title="No orders yet"
          description="Order from a cafe, restaurant, or pharmacy and it'll show up here."
        />
      ) : (
        <>
          {orders.length > 0 && (
            <div className="flex flex-col gap-4" data-testid="my-orders-list">
              {orders.map((o) => (
                <Card key={o.id} data-testid={`my-order-${o.id}`}>
                  <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border p-4">
                    <div className="flex items-center gap-2">
                      {o.fulfillment_type === 'DELIVERY' ? <Bike className="h-5 w-5 text-primary" /> : <Package className="h-5 w-5 text-primary" />}
                      <StatusPill tone={STATUS_TONE[o.status]}>{o.status.replace(/_/g, ' ').toLowerCase()}</StatusPill>
                      <span className="text-xs text-muted-foreground">{fmt(o.created_at)}</span>
                    </div>
                    <span className="font-heading text-lg font-bold tabular-nums">₹{o.total_inr}</span>
                  </div>
                  <div className="p-4">
                    <p className="mb-2 text-sm text-muted-foreground">{STATUS_COPY[o.status]}</p>
                    <ul className="flex flex-col gap-1 text-sm">
                      {o.items.map((it) => (
                        <li key={it.id} className="flex justify-between">
                          <span>{it.quantity}× {it.name}</span>
                          <span className="tabular-nums text-muted-foreground">₹{it.line_total_inr}</span>
                        </li>
                      ))}
                    </ul>
                    {o.cancel_reason && <p className="mt-2 text-xs text-muted-foreground">Reason: {o.cancel_reason}</p>}
                    {o.status === 'PLACED' && (
                      <Button size="sm" variant="ghost" className="mt-3" disabled={cancel_m.isPending} onClick={() => cancel(o.id)} data-testid={`cancel-order-${o.id}`}>
                        Cancel order
                      </Button>
                    )}
                  </div>
                </Card>
              ))}
            </div>
          )}

          {commerce_orders.length > 0 && (
            <div className="flex flex-col gap-4" data-testid="my-commerce-orders-list">
              <h3 className="font-heading text-sm font-bold uppercase tracking-wide text-muted-foreground">Shop orders</h3>
              {commerce_orders.map((o) => (
                <Card key={o.id} data-testid={`my-commerce-order-${o.id}`}>
                  {/*
                    Show the ORDER's own status while it is unpaid, not the shipment rollup.
                    `rollup_status` is derived from shipments, which are created at PLACED
                    before payment — so an unpaid order rendered as "placed / in progress",
                    indistinguishable from a confirmed one (BUG-047).
                  */}
                  <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border p-4">
                    <div className="flex items-center gap-2">
                      <Truck className="h-5 w-5 text-primary" />
                      <StatusPill tone="neutral">
                        {o.vertical === null ? 'Food & Pharmacy' : o.vertical === 'PHARMACY' ? 'Pharmacy' : 'Food'}
                      </StatusPill>
                      <StatusPill tone={COMMERCE_STATUS_TONE[commerce_display_status(o)]}>
                        {commerce_display_status(o).replace(/_/g, ' ').toLowerCase()}
                      </StatusPill>
                      {o.prescription_required && (
                        <StatusPill tone="warning" data-testid={`my-commerce-order-rx-${o.id}`}>
                          Rx
                        </StatusPill>
                      )}
                      <span className="text-xs text-muted-foreground">{fmt(o.created_at)}</span>
                    </div>
                    <span className="font-heading text-lg font-bold tabular-nums">₹{o.grand_total_inr}</span>
                  </div>
                  <div className="p-4">
                    <p className="mb-3 text-sm text-muted-foreground">{COMMERCE_ROLLUP_COPY[commerce_display_status(o)]}</p>

                    <div className="mb-3 flex flex-col gap-1 rounded-input border-2 border-brand-outline-variant/15 bg-secondary/30 p-3 text-sm">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Goods total</span>
                        <span className="tabular-nums">₹{o.goods_total_inr}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Delivery fee</span>
                        <span className="tabular-nums">₹{o.delivery_fee_inr}</span>
                      </div>
                      <div className="mt-1 flex justify-between border-t border-brand-outline-variant/15 pt-1.5 font-heading font-bold">
                        <span>Grand total</span>
                        <span className="tabular-nums">₹{o.grand_total_inr}</span>
                      </div>
                    </div>

                    {o.prescriptions.length > 0 && (
                      <div className="mb-3" data-testid={`my-commerce-order-prescription-${o.id}`}>
                        <p className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Prescription</p>
                        <ImageCarousel images={o.prescriptions} />
                      </div>
                    )}

                    <div className="flex flex-col gap-3" data-testid={`my-commerce-order-shipments-${o.id}`}>
                      {o.shipments.map((s, i) => (
                        <CommerceShipmentCard key={s.id} order={o} shipment={s} index={i} total={o.shipments.length} />
                      ))}
                    </div>

                    {o.cancel_reason && <p className="mt-3 text-xs text-muted-foreground">Reason: {o.cancel_reason}</p>}

                    <p className="mt-3 rounded-input border-2 border-dashed border-brand-outline-variant/30 p-2 text-center text-xs text-muted-foreground">
                      Courier charges are borne by the vendor and settled at delivery.
                    </p>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
